import _ from "lodash";
import {PROCESS_PATHS, REGION, STAGE, TASK_PRIORITY, TASK_TYPE, EMPTY_STRING, COMMA, COLON} from "./Constants";
import {publishClientSideMetrics, getDocumentURL} from "../handlers/ParentDataHandler";

const moment = require('moment-timezone');
const ERROR = "Error";
const UNDERSCORE = "_";

/**************
 *  utilities *
 **************/

export function getStage() {
  const url = window.location.hostname || window.location.href || "";
  if (url.includes("prod")) return STAGE.PROD;
  if (url.includes("gamma")) return STAGE.GAMMA;
  return STAGE.BETA;
}

export function getRegion() {
  const url = window.location.hostname || window.location.href || "";
  if (url.includes("jp")) return REGION.JP;
  if (url.includes("eu")) return REGION.EU;
  if (url.includes("in")) return REGION.EU;
  return REGION.NA;
}

export function getStationCode() {
  const params = new URLSearchParams(window.location.search);
  return params.get('stationCode');
}

export const getRefreshInterval = () => {
  return 60 * 1000;
}

/**
 * Gets the package search URL dynamically from parent window
 * @returns {Promise<string>} Promise that resolves to the search URL
 */
export const getPackageSearchUrl = async () => {
  const documentUrl = await getDocumentURL();
  // Replace 'workitemsvisibility' with 'search' in the parent URL
  const searchURL = new URL(documentUrl.toString().replace('workitemsvisibility', 'search'));
  return searchURL.href;
};

function publishMetricsByNode(args,isCount) {
  let stationCodeSuffix = UNDERSCORE + getStationCode();
  args.metricName += stationCodeSuffix;
  if(isCount){
    publishCountToCSM(args)
  }
  else{
    publishToCSM(args)
  }
}

export function logOperationalMetric(startTime, processName, metricName, isSuccess, isCount) {
  if (isSuccess) {
    let args = {
      startTime: startTime,
      processName: processName,
      metricName: metricName
    };
    if (!isCount){
      publishToCSM(args);
      publishMetricsByNode(args,isCount);
    } else {
      publishCountToCSM(args);
      publishMetricsByNode(args,isCount);
    }
  } else {
    publishCountToCSM({
      processName: processName,
      metricName: metricName + ERROR
    })
  }
}

function publishToCSM(args) {
  let metricValue = getTimeDiff(args.startTime);
  const timeMetricReq = publishClientSideMetricsRequest(args.processName, args.metricName, metricValue, metricTypeEnum.TIME);
  publishClientSideMetrics(timeMetricReq);
}

export function publishCountToCSM(args) {
  const countMetricReq = publishClientSideMetricsRequest(args.processName, args.metricName , 1, metricTypeEnum.COUNT);
  publishClientSideMetrics(countMetricReq);
}

function publishClientSideMetricsRequest(processName, metricName, metricValue, metricType) {
  return {
    processName: processName,
    metricName: metricName,
    metricValue: metricValue,
    metricType: metricType
  }
}

function getTimeDiff(time) {
  return Date.now() - time ;
}

const metricTypeEnum = {
  COUNT: "count",
  TIME: "time"
};

export const isChromeBrowser = () => {
  return navigator.userAgent && (navigator.userAgent.includes("Chrome") || navigator.userAgent.includes("Chromium"));
}

/**
 * Calculates time until expiry given due date time
 * @param {Integer} dueDateTime unix Timestamp in seconds
 * @returns {Integer}
 */
export const getExpiry = (dueDateTime) => {
  const createdAt = new Date();
  const dueAt = new Date(dueDateTime * 1000);
  return (dueAt - createdAt) / 1000 / 60 / 60;
};

/**************
 *    Mapper  *
 **************/

export const getTranslatedProcessName = (process, intl) => {
  process = (process || "").toString().toUpperCase();
  if (!PROCESS_PATHS[process]) return process;
  return intl.formatMessage({
    id: `process.${process}`,
    defaultMessage: process
  });
}

export const getTranslatedTaskTypeName = (taskType, intl) => {
  if (!taskType) return "";
  if (!TASK_TYPE[taskType]) return taskType;
  return intl.formatMessage({
    id: `taskType.${taskType}`,
    defaultMessage: taskType
  });
}

export const shouldExpandRow = (task) => {
  if (!task.additionalAttributes) {
    return false;
  }

  return task.type !== TASK_TYPE.HELP_REQUEST;
}
/**
 * Gets the effective priority value from task, prioritizing CRITICALITY over task.priority
 * @param {object} task - The task object containing priority and additionalAttributes
 * @returns {string} The effective priority value
 */
export const getEffectivePriority = (task) => {
  if (task.additionalAttributes && 
      task.additionalAttributes.CRITICALITY && 
      task.additionalAttributes.CRITICALITY.attributeValue) {
    return task.additionalAttributes.CRITICALITY.attributeValue;
  }
  
  return task.priority;
};

/**
 * Checks if a priority value is considered elevated severity
 * @param {string} priority - The priority value to check
 * @returns {boolean} True if the priority is HIGH, AT_RISK, LATE, or CRITICAL
 */
export const isElevatedSeverity = (priority) => {
    return priority === TASK_PRIORITY.HIGH || 
           priority === TASK_PRIORITY.AT_RISK || 
           priority === TASK_PRIORITY.LATE || 
           priority === TASK_PRIORITY.CRITICAL;
};

/**
 * Takes a priority and maps it to json containing corresponding colors and labels
 * @param {string} priority
 * @param {object} intl
 * @returns {json}
 */
export const priorityMapper = (priority, intl) => {
  const label = intl.formatMessage({
    id: `priority.${priority ? priority.toString().toLowerCase() : ""}`,
    defaultMessage: "None"
  });
  const value = priority;
  switch (priority) {
    case TASK_PRIORITY.HIGH:
    case TASK_PRIORITY.AT_RISK:
    case TASK_PRIORITY.LATE:
    case TASK_PRIORITY.CRITICAL:
      return {
        value,
        label,
        status: "error",
        color: "#CC0C39",
      };
    case TASK_PRIORITY.MEDIUM:
      return {
        value,
        label,
        status: "warning",
        color: "#000000",
      };
    case TASK_PRIORITY.LOW:
      return {
        value,
        label,
        status: "neutral",
        color: "#000000",
      };
    default:
      return {
        value,
        label,
        status: "neutral",
        color: "#000000",
      };
  }
};

export const getTaskName = (task) => {
  let taskName = "";
  if (task.name) {
    taskName = _.tail(task.name.split("_")).join(" ");
  }
  return taskName;
}

/**************
 *   filter*  *
 **************/

export const getExpiredTasks = (tasks) => {
  return tasks.filter(task => {
    if (!task.dueDateTime) return false;
    return getExpiry(task.dueDateTime) <= 0;
  });
}

export const getPendingTasks = (tasks) => {
  return tasks.filter(task => {
    if (!task.dueDateTime) return true;
    return getExpiry(task.dueDateTime) > 0;
  });
}

export const filterTasksByCategories = (tasks, taskCategories) => {
  return tasks.filter((task) => taskCategories.includes((task.category || "").toUpperCase()));
}

export const filterTasksByExpiry = (tasks, expiry) => {
  return tasks.filter((task) => task.dueDateTime && getExpiry(task.dueDateTime) < expiry);
}

export const filterTasksByAssignees = (tasks, assignees) => {
  return tasks.filter((task) => assignees.includes(task.assignee));
}

export const filterTasksByAssociateName = (tasks, associateName) => {
  return tasks.filter((task) => {
    return (
      task.hasOwnProperty('additionalAttributes') &&
      task.additionalAttributes.hasOwnProperty('employeeLogin') &&
      task.additionalAttributes.employeeLogin.attributeValue.toLowerCase().includes(associateName.toLowerCase()))
  });
}
export const filterTasksByManagerName = (tasks, managerName) => {
  return tasks.filter((task) => {
    return (
      task.hasOwnProperty('additionalAttributes') &&
      task.additionalAttributes.hasOwnProperty('manager') &&
      task.additionalAttributes.manager.attributeValue.toLowerCase().includes(managerName.toLowerCase()))
  });
}

export const filterTasksByPriority = (tasks, selectedPriorities, intl) => {
  return tasks.filter((task) => {
    const effectivePriority = getEffectivePriority(task);
    return (
        selectedPriorities.some((selectedPriority) => selectedPriority === effectivePriority)
    );
  })
}

/** 
 * 
 * @param {*} tasks : List of tasks to be filtered 
 * @param {*} taskName : criteria to filter the task by task name
 * @returns 
 */
export const filterTasksByName = (tasks, taskName) => {
  return tasks.filter((task) => {
    return (
      getTaskName(task).toLowerCase().includes(taskName.toLowerCase())
    );
  })
}

/** 
 * 
 * @param {*} tasks : List of tasks to be filtered 
 * @param {*} taskName : criteria to filter the task by process name
 * @returns 
 */
export const filterTasksByProcessName = (tasks, processName, intl) => {
  return tasks.filter((task) => {
    return (
      getTranslatedProcessName(task.category, intl).toLowerCase().includes(processName.toLowerCase())
    );
  })
}

/** 
 * 
 * @param {*} tasks : List of tasks to be filtered 
 * @param {*} taskName : criteria to filter the task by task type
 * @returns 
 */
export const filterTasksByTaskType = (tasks, taskType, intl) => {
  return tasks.filter((task) => {
    return (
      getTranslatedTaskTypeName(task.type, intl).toLowerCase().includes(taskType.toLowerCase())
    );
  })
}

/** 
 * 
 * @param {*} tasks : List of tasks to be filtered 
 * @param {*} taskName : criteria to filter the task by assignee
 * @returns 
 */
export const filterTasksByStartedBy = (tasks, startedBy) => {
  return tasks.filter((task) => {
    return (
      task.assignee != null && task.assignee.toLowerCase().includes(startedBy.toLowerCase())
    )
  });
}


/**************
 *   Sorting  *
 **************/

const PRIORITY_TO_VAL = {
  "CRITICAL": 1,
  "AT_RISK": 2,
  "LATE": 3,
  "HIGH": 4,
  "MEDIUM": 5,
  "LOW": 6
};


export const compareTasksByPriority = (a, b, isAscending) => {
  const aEffectivePriority = getEffectivePriority(a);
  const bEffectivePriority = getEffectivePriority(b);
  
  if (isAscending) {
    return PRIORITY_TO_VAL[aEffectivePriority] - PRIORITY_TO_VAL[bEffectivePriority];
  }
  return PRIORITY_TO_VAL[bEffectivePriority] - PRIORITY_TO_VAL[aEffectivePriority];
}

export const compareTasksByExpiry = (a, b, isAscending) => {
  if (!a.dueDateTime && !b.dueDateTime) return 0;
  if (!a.dueDateTime) return 1;
  if (!b.dueDateTime) return -1;
  if (isAscending) return getExpiry(a.dueDateTime) - getExpiry(b.dueDateTime);
  return getExpiry(b.dueDateTime) - getExpiry(a.dueDateTime);
}

export const compareTasksByName = (a, b, isAscending) => {
  const aTaskName = getTaskName(a);
  const bTaskName = getTaskName(b);
  
  // First compare by task name (exception type)
  const nameComparison = isAscending 
    ? aTaskName.localeCompare(bTaskName)
    : bTaskName.localeCompare(aTaskName);
  
  // If task names are the same, sort by priority (severity) within the group
  if (nameComparison === 0) {
    return compareTasksByPriority(a, b, false); // Always sort by priority desc within same task name
  }
  
  return nameComparison;
}

/**************
 *   moment   *
 **************/

export const isValidTimezone = (timezone) => {
  return !!moment.tz.zone(timezone);
}

/**
 * Check is timestamp is valid unix timestamp in milliseconds
 * @param timestamp
 * @returns {boolean}
 */
export const isValidTimestamp = (timestamp) => {
  if (!timestamp) return false;
  if (Number.isNaN(Number(timestamp))) return false;
  return !Number.isNaN(new Date(Number(timestamp)).getTime())
}

/**
 * Parses a sopParameters string of comma-separated "key:value" pairs into a map.
 * E.g. "criticality:high,flag:true" => { criticality: "high", flag: "true" }
 * @param {Object} task
 * @returns {Object}
 */
export const getSopParameters = (task) => {
  const attributeValue = task && task.additionalAttributes && task.additionalAttributes.sopParameters
    ? task.additionalAttributes.sopParameters.attributeValue
    : null;
  return (attributeValue || EMPTY_STRING).split(COMMA)
    .filter(Boolean)
    .reduce((acc, flag) => {
      const parts = flag.split(COLON);
      if (parts.length === 2) {
        acc[parts[0].trim()] = parts[1].trim();
      }
      return acc;
    }, {});
};



// WEBPACK FOOTER //
// ./src/utils/Helper.js